import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wrench, Package, Radio, Plane, Heart, Target, X, ChevronLeft } from 'lucide-react';
import HUDFrame from './ui/HUDFrame';
import GlowButton from './ui/GlowButton';
import MediaGallery from './ui/MediaGallery';
import { MOSOption } from '../types';
import { MOS_OPTIONS } from '../constants';
import ArmyLogo from './united-states-army-2023-seeklogo.png';

interface MOSSelectionProps {
  onSelect: (mos: MOSOption) => void;
  onBack?: () => void;
  onCardClick?: () => void;
  onModalSelect?: () => void;
}

const iconMap: { [key: string]: React.FC<{ className?: string }> } = {
  Wrench,
  Package,
  Radio,
  Plane,
  Heart,
  Target,
};

// MOS card background images hosted on R2 CDN (optimized WebP)
const backgroundImageMap: { [key: string]: string } = {
  aviation: 'https://storage.afs.vustudio.network/app/army/mos-cards/aviation-card.webp',
  medical: 'https://storage.afs.vustudio.network/app/army/mos-cards/medical-card.webp',
  infantry: 'https://storage.afs.vustudio.network/app/army/mos-cards/infantry-card.webp',
  logistics: 'https://storage.afs.vustudio.network/app/army/mos-cards/logistics-card.webp',
  signal: 'https://storage.afs.vustudio.network/app/army/mos-cards/signal-card.webp',
  mechanics: 'https://storage.afs.vustudio.network/app/army/mos-cards/mechanics-card.webp',
};

const MOSSelection: React.FC<MOSSelectionProps> = ({ onSelect, onBack, onCardClick, onModalSelect }) => {
  const [selectedMOS, setSelectedMOS] = useState<MOSOption | null>(null);
  const [infoMOS, setInfoMOS] = useState<MOSOption | null>(null);

  const handleConfirm = () => {
    if (selectedMOS) {
      onSelect(selectedMOS);
    }
  };

  const handleOpenModal = (mos: MOSOption) => {
    // Play card swipe sound and fade music
    onCardClick?.();
    setInfoMOS(mos);
  };

  const handleCloseModal = () => {
    setInfoMOS(null);
  };

  const handleSelectFromModal = () => {
    if (infoMOS) {
      // Play selection sound
      onModalSelect?.();
      setSelectedMOS(infoMOS);
      setInfoMOS(null);
      // Automatically proceed to next page
      onSelect(infoMOS);
    }
  };

  return (
    <motion.div
      className="h-full w-full flex items-center justify-center bg-army-black tactical-grid"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <HUDFrame className="w-full max-w-7xl 4k:max-w-[200rem] mx-auto p-6 4k:p-32">
        <div className="text-center space-y-6 4k:space-y-24 pt-8 md:pt-4 4k:pt-12">
          {/* Back Button */}
          {onBack && (
            <motion.button
              onClick={onBack}
              className="absolute left-6 top-20 4k:top-40 flex items-center space-x-2 4k:space-x-4 text-army-gold hover:text-white transition-colors group z-10"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              whileHover={{ x: -5 }}
            >
              <ChevronLeft className="w-6 h-6 4k:w-16 4k:h-16" />
              <span className="text-sm 4k:text-3xl uppercase tracking-wider font-bold">Back</span>
            </motion.button>
          )}

          {/* Army Logo */}
          <motion.div
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.6 }}
          >
            <img src={ArmyLogo} alt="U.S. Army" className="h-20 4k:h-64 w-auto mx-auto" />
          </motion.div>
          {/* Header */}
          <motion.div
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <h2 className="text-2xl 4k:text-8xl text-army-gold uppercase tracking-widest mb-2 4k:mb-10">
              MOS.AIC
            </h2>
            <h1 className="text-4xl 4k:text-[12rem] font-bold text-white leading-none">
              SELECT YOUR PATH
            </h1>
          </motion.div>

          {/* Progress indicator */}
          <motion.div
            className="flex items-center justify-center space-x-3 4k:space-x-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <div className="w-16 4k:w-32 h-1.5 4k:h-3 bg-army-gold" />
            <div className="w-16 4k:w-32 h-1.5 4k:h-3 bg-army-gold" />
            <div className="w-16 4k:w-32 h-1.5 4k:h-3 bg-army-gold" />
            <div className="w-16 4k:w-32 h-1.5 4k:h-3 bg-gray-600" />
            <div className="w-16 4k:w-32 h-1.5 4k:h-3 bg-gray-600" />
          </motion.div>

          {/* MOS Cards - 6 options in 2 rows of 3 */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 4k:gap-10 mt-6 4k:mt-12">
            {MOS_OPTIONS.map((mos, index) => {
              const Icon = iconMap[mos.icon];
              const isSelected = selectedMOS?.id === mos.id;
              const bgImage = backgroundImageMap[mos.id];

              return (
                <motion.div
                  key={mos.id}
                  className={`group relative cursor-pointer ${isSelected ? 'scale-[1.02]' : ''}`}
                  initial={{ y: 100, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 + index * 0.08, duration: 0.5 }}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => handleOpenModal(mos)}
                >
                  <div
                    className={`relative overflow-hidden border-3 4k:border-8 p-5 4k:p-14 transition-all h-full flex flex-col items-center justify-center ${
                      isSelected
                        ? 'border-white shadow-2xl shadow-army-gold/30'
                        : 'border-army-gold/70 hover:border-army-gold'
                    } ${!bgImage ? 'bg-gradient-to-b from-army-dark to-army-black' : ''}`}
                  >
                    {/* Background Image */}
                    {bgImage && (
                      <>
                        <div
                          className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                          style={{ backgroundImage: `url(${bgImage})` }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/40" />
                      </>
                    )}
                    {/* Selected indicator */}
                    {isSelected && (
                      <motion.div
                        className="absolute -top-4 -right-4 4k:-top-8 4k:-right-8 bg-army-gold text-black px-3 py-1.5 4k:px-6 4k:py-3 text-sm 4k:text-2xl font-bold uppercase z-10"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                      >
                        Selected
                      </motion.div>
                    )}

                    {/* Icon */}
                    <div className="relative z-10 flex justify-center mb-4 4k:mb-8">
                      <div className="relative">
                        <Icon className="w-14 h-14 4k:w-56 4k:h-56 text-army-gold drop-shadow-lg" />
                        {isSelected && (
                          <motion.div
                            className="absolute inset-0 bg-army-gold opacity-30 blur-xl"
                            animate={{
                              scale: [1, 1.3, 1],
                            }}
                            transition={{
                              duration: 2,
                              repeat: Infinity,
                            }}
                          />
                        )}
                      </div>
                    </div>

                    {/* Title */}
                    <h3 className="relative z-10 text-xl 4k:text-7xl font-bold text-white uppercase mb-6 4k:mb-14 drop-shadow-lg leading-none">
                      {mos.title}
                    </h3>

                    {/* Corner accents */}
                    <div className="absolute top-0 left-0 w-4 h-4 4k:w-12 4k:h-12 border-t-3 border-l-3 4k:border-t-8 4k:border-l-8 border-army-gold z-10" />
                    <div className="absolute top-0 right-0 w-4 h-4 4k:w-12 4k:h-12 border-t-3 border-r-3 4k:border-t-8 4k:border-r-8 border-army-gold z-10" />
                    <div className="absolute bottom-0 left-0 w-4 h-4 4k:w-12 4k:h-12 border-b-3 border-l-3 4k:border-b-8 4k:border-l-8 border-army-gold z-10" />
                    <div className="absolute bottom-0 right-0 w-4 h-4 4k:w-12 4k:h-12 border-b-3 border-r-3 4k:border-b-8 4k:border-r-8 border-army-gold z-10" />
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Confirm Button */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: selectedMOS ? 1 : 0.3 }}
            transition={{ duration: 0.3 }}
          >
            <GlowButton
              onClick={handleConfirm}
              disabled={!selectedMOS}
              variant="primary"
              size="huge"
            >
              Confirm Selection
            </GlowButton>
          </motion.div>

          {/* Bottom Status */}
          <motion.div
            className="pt-4 4k:pt-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.6 }}
            transition={{ delay: 1 }}
          >
            <p className="text-base 4k:text-3xl text-gray-500 uppercase tracking-widest">
              Step 3 of 5 • Specialty Selection
            </p>
          </motion.div>
        </div>
      </HUDFrame>

      {/* Info Modal */}
      <AnimatePresence>
        {infoMOS && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseModal}
          >
            <motion.div
              className="relative bg-gradient-to-b from-army-dark to-army-black border-4 4k:border-8 border-army-gold p-8 4k:p-20 max-w-4xl 4k:max-w-[100rem] w-full max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={handleCloseModal}
                className="absolute top-6 right-6 4k:top-10 4k:right-10 text-gray-400 hover:text-white transition-colors z-10"
              >
                <X className="w-8 h-8 4k:w-16 4k:h-16" />
              </button>

              {/* Modal Content */}
              <div className="space-y-8 4k:space-y-16">
                {/* Header Section */}
                <div className="flex items-start gap-6 4k:gap-12">
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    {(() => {
                      const Icon = iconMap[infoMOS.icon];
                      return (
                        <div className="w-20 h-20 4k:w-64 4k:h-64 bg-army-gold/10 border-2 4k:border-6 border-army-gold/50 flex items-center justify-center">
                          <Icon className="w-12 h-12 4k:w-48 4k:h-48 text-army-gold" />
                        </div>
                      );
                    })()}
                  </div>

                  {/* Title and Description */}
                  <div className="flex-1">
                    <h2 className="text-3xl 4k:text-9xl font-bold text-white uppercase leading-none">
                      {infoMOS.shortTitle}
                    </h2>
                    <p className="text-army-gold mt-2 4k:mt-6 text-lg 4k:text-6xl">{infoMOS.title}</p>
                    <p className="text-gray-300 text-base 4k:text-5xl leading-relaxed mt-4 4k:mt-12">
                      {infoMOS.detailedDescription}
                    </p>
                  </div>
                </div>

                {/* Media Gallery */}
                <div>
                  <h3 className="text-sm text-gray-400 uppercase tracking-wider mb-3">
                    Gallery
                  </h3>
                  <MediaGallery items={infoMOS.media} />
                </div>

                {/* Stats */}
                <div>
                  <h3 className="text-base 4k:text-3xl text-gray-400 uppercase tracking-wider mb-4 4k:mb-8">
                    Specialty Attributes
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-5 4k:gap-10">
                    <div>
                      <p className="text-sm 4k:text-2xl text-gray-500 uppercase mb-2 4k:mb-4">Strategy</p>
                      <div className="h-3 4k:h-6 bg-gray-700">
                        <motion.div
                          className="h-full bg-army-gold"
                          initial={{ width: 0 }}
                          animate={{ width: `${infoMOS.stats.strategy}%` }}
                          transition={{ duration: 0.5, delay: 0.1 }}
                        />
                      </div>
                      <p className="text-sm 4k:text-2xl text-army-gold mt-2 4k:mt-3">{infoMOS.stats.strategy}%</p>
                    </div>
                    <div>
                      <p className="text-sm 4k:text-2xl text-gray-500 uppercase mb-2 4k:mb-4">Technology</p>
                      <div className="h-3 4k:h-6 bg-gray-700">
                        <motion.div
                          className="h-full bg-army-gold"
                          initial={{ width: 0 }}
                          animate={{ width: `${infoMOS.stats.technology}%` }}
                          transition={{ duration: 0.5, delay: 0.2 }}
                        />
                      </div>
                      <p className="text-sm 4k:text-2xl text-army-gold mt-2 4k:mt-3">{infoMOS.stats.technology}%</p>
                    </div>
                    <div>
                      <p className="text-sm 4k:text-2xl text-gray-500 uppercase mb-2 4k:mb-4">Leadership</p>
                      <div className="h-3 4k:h-6 bg-gray-700">
                        <motion.div
                          className="h-full bg-army-gold"
                          initial={{ width: 0 }}
                          animate={{ width: `${infoMOS.stats.leadership}%` }}
                          transition={{ duration: 0.5, delay: 0.3 }}
                        />
                      </div>
                      <p className="text-sm 4k:text-2xl text-army-gold mt-2 4k:mt-3">{infoMOS.stats.leadership}%</p>
                    </div>
                    <div>
                      <p className="text-sm 4k:text-2xl text-gray-500 uppercase mb-2 4k:mb-4">Endurance</p>
                      <div className="h-3 4k:h-6 bg-gray-700">
                        <motion.div
                          className="h-full bg-army-gold"
                          initial={{ width: 0 }}
                          animate={{ width: `${infoMOS.stats.endurance}%` }}
                          transition={{ duration: 0.5, delay: 0.4 }}
                        />
                      </div>
                      <p className="text-sm 4k:text-2xl text-army-gold mt-2 4k:mt-3">{infoMOS.stats.endurance}%</p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-6 4k:space-x-10 pt-4 4k:pt-8">
                  <button
                    onClick={handleCloseModal}
                    className="flex-1 px-6 py-4 4k:px-10 4k:py-8 border-2 4k:border-4 border-gray-600 text-gray-300 hover:border-white hover:text-white transition-colors uppercase text-base 4k:text-3xl font-bold"
                  >
                    Close
                  </button>
                  <button
                    onClick={handleSelectFromModal}
                    className="flex-1 px-6 py-4 4k:px-10 4k:py-8 bg-army-gold text-black hover:bg-yellow-400 transition-colors uppercase text-base 4k:text-3xl font-bold"
                  >
                    Select This Specialty
                  </button>
                </div>
              </div>

              {/* Corner accents */}
              <div className="absolute top-0 left-0 w-6 h-6 4k:w-16 4k:h-16 border-t-4 border-l-4 4k:border-t-8 4k:border-l-8 border-army-gold" />
              <div className="absolute top-0 right-0 w-6 h-6 4k:w-16 4k:h-16 border-t-4 border-r-4 4k:border-t-8 4k:border-r-8 border-army-gold" />
              <div className="absolute bottom-0 left-0 w-6 h-6 4k:w-16 4k:h-16 border-b-4 border-l-4 4k:border-b-8 4k:border-l-8 border-army-gold" />
              <div className="absolute bottom-0 right-0 w-6 h-6 4k:w-16 4k:h-16 border-b-4 border-r-4 4k:border-b-8 4k:border-r-8 border-army-gold" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default MOSSelection;
